import React, { useState, useEffect, useMemo } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import "../styles/DonationPage.css";

export default function DonationPage() {
  const [donationAmount, setDonationAmount] = useState("");
  const [userInfo, setUserInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [paypalClientId, setPaypalClientId] = useState(null);
  const [isLoadingPaypal, setIsLoadingPaypal] = useState(true);

  // Si ton backend est servi sur le même domaine que le front (nginx/proxy),
  // les routes relatives /api/... sont parfaites.
  const API_CREATE_ORDER = "/api/paypal/create-donation";
  const API_CAPTURE_ORDER = "/api/paypal/capture-payment";

  // 🟦 Charger le client-id PayPal depuis le backend
  useEffect(() => {
    const fetchClientId = async () => {
      try {
        const res = await fetch("/api/paypal/client-id");
        const data = await res.json();

        if (!res.ok || !data.clientId) {
          throw new Error(
            data?.error || "Client ID PayPal introuvable sur le serveur."
          );
        }

        setPaypalClientId(data.clientId);
      } catch (err) {
        console.error("❌ Erreur chargement client-id PayPal :", err);
        setErrorMessage(
          "Impossible de charger PayPal pour le moment. Veuillez réessayer plus tard."
        );
      } finally {
        setIsLoadingPaypal(false);
      }
    };

    fetchClientId();
  }, []);

  // Options PayPal, mémorisées pour éviter des re-renders inutiles
  const paypalOptions = useMemo(() => {
    if (!paypalClientId) return null;
    return {
      "client-id": paypalClientId,
      currency: "EUR",
    };
  }, [paypalClientId]);

  const handleDonationChange = (e) => {
    const value = e.target.value;
    if (/^[0-9]*(\.[0-9]*)?$/.test(value)) {
      setDonationAmount(value);
    }
  };

  // 🧾 Création de la commande (appel backend)
  const createOrder = async () => {
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const amount = parseFloat(donationAmount);
      if (isNaN(amount) || amount <= 0) {
        throw new Error("Veuillez saisir un montant valide.");
      }

      if (!userInfo.firstName || !userInfo.lastName || !userInfo.email) {
        throw new Error("Veuillez remplir prénom, nom et email.");
      }

      const payload = {
        donation_amount: amount.toFixed(2),
        first_name: userInfo.firstName,
        last_name: userInfo.lastName,
        email: userInfo.email,
      };

      console.log("🟢 Envoi des données à /create-donation :", payload);

      const response = await fetch(API_CREATE_ORDER, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const rawText = await response.text();
      console.log("📥 Réponse brute /create-donation :", rawText);

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${rawText}`);
      }

      let orderData;
      try {
        orderData = JSON.parse(rawText);
      } catch (e) {
        console.error(
          "❌ Réponse NON JSON du backend /create-donation :",
          rawText
        );
        throw new Error("Réponse invalide du serveur (create-donation).");
      }

      if (!orderData.id) {
        throw new Error("ID de commande manquant dans la réponse du serveur.");
      }

      console.log("✅ ID de commande PayPal (donation) :", orderData.id);
      return orderData.id; // important pour PayPal
    } catch (error) {
      console.error("❌ Error creating order:", error);
      setErrorMessage(error.message);
      setIsSubmitting(false);
      // On relance l'erreur pour que PayPal sache que la création a échoué
      throw error;
    }
  };

  // ✅ Capture du paiement après validation PayPal
  const onApprove = async (data) => {
    try {
      console.log("✅ Paiement approuvé (donation) :", data);

      const payload = { orderId: data.orderID };
      console.log("📌 Payload pour /capture-payment :", payload);

      const response = await fetch(API_CAPTURE_ORDER, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const rawText = await response.text();
      console.log("📥 Réponse brute /capture-payment (donation) :", rawText);

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${rawText}`);
      }

      let captureData;
      try {
        captureData = JSON.parse(rawText);
      } catch (e) {
        throw new Error(
          `Réponse JSON invalide de /capture-payment : ${rawText}`
        );
      }

      console.log("✅ Paiement capturé (donation) :", captureData);

      setIsModalOpen(true);
    } catch (error) {
      console.error("❌ Error capturing order:", error);
      setErrorMessage(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCancel = () => {
    console.log("ℹ️ Transaction PayPal annulée par l'utilisateur.");
    setIsCancelModalOpen(true);
    setIsSubmitting(false);
  };

  const onError = (err) => {
    console.error("❌ PayPal Error:", err);
    setErrorMessage("Une erreur est survenue avec PayPal.");
    setIsSubmitting(false);
  };

  return (
    <div className="donation-page">
      <div className="donation-text">
        <p>
          Chers visiteurs,
          <br />
          <br />
          Bienvenue sur notre page dédiée à la solidarité... (rest of your text)
        </p>
      </div>

      <div className="donation-container">
        <h2>Faites un don</h2>

        {errorMessage && <p className="error-message">{errorMessage}</p>}

        <form className="donation-form" onSubmit={(e) => e.preventDefault()}>
          <div className="form-group">
            <label htmlFor="firstName">Prénom</label>
            <input
              id="firstName"
              type="text"
              value={userInfo.firstName}
              onChange={(e) =>
                setUserInfo({ ...userInfo, firstName: e.target.value })
              }
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="lastName">Nom</label>
            <input
              id="lastName"
              type="text"
              value={userInfo.lastName}
              onChange={(e) =>
                setUserInfo({ ...userInfo, lastName: e.target.value })
              }
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={userInfo.email}
              onChange={(e) =>
                setUserInfo({ ...userInfo, email: e.target.value })
              }
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="donationAmount">Montant du don (€)</label>
            <input
              id="donationAmount"
              type="text"
              value={donationAmount}
              onChange={handleDonationChange}
              placeholder="Ex: 10.00"
            />
          </div>

          {/* Affichage de chargement PayPal */}
          {isLoadingPaypal && (
            <p
              style={{
                textAlign: "center",
                marginTop: "10px",
                color: "#666",
              }}
            >
              Chargement de PayPal…
            </p>
          )}

          {/* Affichage du bouton PayPal uniquement si le client-id est prêt */}
          {!isLoadingPaypal && paypalOptions && (
            <PayPalScriptProvider options={paypalOptions}>
              {donationAmount && parseFloat(donationAmount) > 0 ? (
                <PayPalButtons
                  style={{ layout: "vertical" }}
                  createOrder={createOrder}
                  onApprove={onApprove}
                  onCancel={onCancel}
                  onError={onError}
                  disabled={isSubmitting}
                  forceReRender={[
                    donationAmount,
                    userInfo.firstName,
                    userInfo.lastName,
                    userInfo.email,
                  ]}
                />
              ) : (
                <p
                  style={{
                    textAlign: "center",
                    marginTop: "10px",
                    color: "#666",
                  }}
                >
                  Veuillez entrer un montant pour afficher les options de
                  paiement.
                </p>
              )}
            </PayPalScriptProvider>
          )}
        </form>
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Merci pour votre générosité !</h2>
            <p>Votre donation a bien été prise en compte.</p>
            <button onClick={() => (window.location.href = "/")}>OK</button>
          </div>
        </div>
      )}

      {isCancelModalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Transaction annulée</h2>
            <button onClick={() => setIsCancelModalOpen(false)}>Fermer</button>
          </div>
        </div>
      )}
    </div>
  );
}
